import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { LangProvider } from "@/lib/lang-context";
import { AuthProvider } from "@/lib/auth-context";
import { QuotaProvider } from "@/lib/quota-context";
import { usePageviewTracking } from "@/lib/analytics";
import { bootstrapConsent } from "@/lib/consent";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import QuotaBanner from "@/components/QuotaBanner";
import PaywallModal from "@/components/PaywallModal";
import CookieBanner from "@/components/CookieBanner";
import OnboardingTour from "@/components/OnboardingTour";
import Home from "@/pages/Home";
import History from "@/pages/History";
import SharedSheet from "@/pages/SharedSheet";
import AuthCallback from "@/pages/AuthCallback";
import Shortlist from "@/pages/Shortlist";
import Plan from "@/pages/Plan";
import Login from "@/pages/Login";
import BillingReturn from "@/pages/BillingReturn";
import Privacy from "@/pages/Privacy";
import CityPage from "@/pages/CityPage";
import FAQ from "@/pages/FAQ";
import Glossary from "@/pages/Glossary";
import OmiHeatMapPage from "@/pages/OmiHeatMapPage";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";

/**
 * Synchronous session_id detection — per Emergent Auth playbook, the fragment
 * check MUST happen during render (not in useEffect) so the callback page
 * exchanges the session BEFORE any other route's data fetch races with it.
 */
function AppRouter({ onOpenPaywall }) {
  const location = useLocation();
  usePageviewTracking();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home onOpenPaywall={onOpenPaywall} />} />
      <Route path="/history" element={<History />} />
      <Route path="/sheet/:id" element={<SharedSheet />} />
      <Route path="/shortlist" element={<Shortlist />} />
      <Route path="/plan" element={<Plan />} />
      <Route path="/login" element={<Login />} />
      <Route path="/billing/return" element={<BillingReturn />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/citta/:slug" element={<CityPage />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/glossario" element={<Glossary />} />
      <Route path="/mappa-prezzi-salento" element={<OmiHeatMapPage />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
    </Routes>
  );
}

function App() {
  // Paywall modal lives at the root so any descendant can open it via the
  // prop drilled into Home + EvaluationSheet.
  const [paywallOpen, setPaywallOpen] = useState(false);
  // Sync persisted GDPR consent to GA + PostHog on every mount.
  useEffect(() => { bootstrapConsent(); }, []);
  return (
    <HelmetProvider>
      <LangProvider>
        <AuthProvider>
          <QuotaProvider>
            <BrowserRouter>
              <div className="App min-h-screen flex flex-col">
                <Header />
                <QuotaBanner onOpenPaywall={() => setPaywallOpen(true)} />
                <div className="flex-1">
                  <AppRouter onOpenPaywall={() => setPaywallOpen(true)} />
                </div>
                <Footer />
                <PaywallModal open={paywallOpen} onClose={() => setPaywallOpen(false)} />
                <CookieBanner />
                <OnboardingTour />
                <Toaster
                  position="top-right"
                  toastOptions={{
                    style: {
                      fontFamily: "Manrope, sans-serif",
                      borderRadius: 0,
                      border: "1px solid #E6E4DD",
                    },
                  }}
                />
              </div>
            </BrowserRouter>
          </QuotaProvider>
        </AuthProvider>
      </LangProvider>
    </HelmetProvider>
  );
}

export default App;
